/* ==========================================================================
   Start Nutra · PDP gallery support
   --------------------------------------------------------------------------
   The PDP media viewer (every gallery item opens it, images and videos) is
   assets/sn-pdp-viewer.js. This file keeps what the gallery itself needs:

   SnPdpGalleryProgress   drives the decorative scroll-fill line's height
                          from scroll position measured against the gallery
                          column's own bounding rect (not the page's scroll
                          percentage). Desktop-only, gated in init().
   SnPdpGalleryVideos     plays the page gallery's videos while visible and
                          pauses them when not; held still while the media
                          viewer is open (sn-pdp-viewer:open / :close).
   initAlphaVideos        WebKit swap for the transparent product video.
   initHeaderHeightSync   keeps --header-height current (also relied on by
                          sn-reviews-scroll.js and sn-molecules-scroll.js).
   SnLightbox             NOT used by the PDP any more. Kept because
                          sn-home-reviews.liquid builds its media lightbox on
                          it (window.SnLightbox, below); this file is loaded
                          there for that reason.
   ========================================================================== */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 1024px)';
  var REDUCED = '(prefers-reduced-motion: reduce)';

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function lockBody() {
    document.body.classList.add('overflow-hidden');
  }
  function unlockBody() {
    document.body.classList.remove('overflow-hidden');
  }

  /* ---------------------------------------------------------------- Lightbox */

  class SnLightbox {
    constructor(root, images) {
      this.root = root;
      this.images = images;
      this.dialog = root.querySelector('[role="dialog"]');
      this.img = root.querySelector('.sn-pdp-lightbox__img');
      // Video is optional: null on markup that has no <video> element, so
      // every use below is guarded.
      this.video = root.querySelector('.sn-pdp-lightbox__video');
      this.stage = root.querySelector('[data-sn-lb-stage]');
      this.currentEl = root.querySelector('[data-sn-lb-current]');
      // Optional: present only where a caller renders a dynamic total
      // (sn-home-reviews, where each review has a different media count).
      this.totalEl = root.querySelector('[data-sn-lb-total]');
      this.thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-sn-lb-thumb]'));
      this.closeBtn = root.querySelector('[data-sn-lb-close]');
      this.prevBtn = root.querySelector('[data-sn-lb-prev]');
      this.nextBtn = root.querySelector('[data-sn-lb-next]');
      this.opener = null;
      this.active = 1;
      this.zoomed = false;
      this.onSync = null; // optional hook; unused now there is no single "active" stage to mirror back to
      this.onKeydown = this.onKeydown.bind(this);
      this.bind();
    }

    bind() {
      var self = this;

      this.root.querySelectorAll('[data-sn-lb-close]').forEach(function (el) {
        el.addEventListener('click', function () { self.close(); });
      });

      // Arrows live inside the stage; stop the click bubbling to the stage's
      // zoom-toggle handler so prev/next only navigate.
      if (this.prevBtn) this.prevBtn.addEventListener('click', function (e) { e.stopPropagation(); self.show(self.active - 1); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function (e) { e.stopPropagation(); self.show(self.active + 1); });

      this.thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          self.show(parseInt(t.dataset.snLbThumb, 10));
        });
      });

      // Deeper zoom inside the lightbox: click toggles, wheel adjusts, pointer pans.
      this.stage.addEventListener('click', function (e) { self.toggleZoom(e); });
      this.stage.addEventListener('pointermove', function (e) { self.pan(e); });
      this.stage.addEventListener(
        'wheel',
        function (e) { self.wheelZoom(e); },
        { passive: false }
      );
    }

    open(index, opener) {
      // No viewport gate here: callers decide (sn-home-reviews opens at
      // any width).
      this.opener = opener || null;
      this.root.hidden = false;
      // Force reflow so the opacity transition runs from hidden.
      void this.root.offsetWidth;
      this.root.dataset.open = 'true';
      lockBody();
      this.show(index);
      document.addEventListener('keydown', this.onKeydown);

      if (typeof window.trapFocus === 'function') {
        window.trapFocus(this.dialog, this.closeBtn);
      } else if (this.closeBtn) {
        this.closeBtn.focus();
      }
    }

    close() {
      this.resetZoom();
      if (this.video) this.video.pause();
      this.root.dataset.open = 'false';
      document.removeEventListener('keydown', this.onKeydown);
      unlockBody();

      var self = this;
      var finish = function () {
        self.root.hidden = true;
        self.root.removeEventListener('transitionend', finish);
      };
      // If reduced-motion (no transition), hide immediately.
      if (window.matchMedia(REDUCED).matches) {
        finish();
      } else {
        this.root.addEventListener('transitionend', finish);
      }

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.opener);
      } else if (this.opener) {
        this.opener.focus();
      }
    }

    show(index) {
      var total = this.images.length;
      // Wrap around for arrow / prev-next.
      if (index < 1) index = total;
      if (index > total) index = 1;
      this.active = index;
      this.resetZoom();

      var data = this.images[index - 1];
      var isVideo = data.type === 'video';
      if (this.stage) this.stage.dataset.media = isVideo ? 'video' : 'image';

      if (isVideo && this.video) {
        if (this.img) this.img.hidden = true;
        this.video.hidden = false;
        this.video.pause();
        this.video.currentTime = 0;
        this.video.muted = false;
        this.video.poster = data.poster || data.full || '';
        // Rebuild the <source> so browsers pick up a changed file cleanly;
        // no <source> at all (e.g. sample/placeholder data with nothing
        // real to play) leaves a poster-only player, which is valid.
        this.video.innerHTML = '';
        if (data.full) {
          var source = document.createElement('source');
          source.src = data.full;
          this.video.appendChild(source);
          this.video.load();
        }
        this.video.setAttribute('aria-label', data.alt || 'Video');
      } else {
        if (this.video) { this.video.pause(); this.video.hidden = true; }
        if (this.img) {
          this.img.hidden = false;
          this.setImage(data.full);
          this.img.alt = data.alt || '';
        }
      }

      if (this.currentEl) this.currentEl.textContent = String(index);
      if (this.totalEl) this.totalEl.textContent = String(total);

      this.thumbs.forEach(function (t) {
        t.setAttribute('aria-selected', parseInt(t.dataset.snLbThumb, 10) === index ? 'true' : 'false');
      });

      // Mirror the lightbox's current image back onto the underlying stage.
      if (this.onSync) this.onSync(index);
    }

    // True when the active item is a video: used to keep the deep-zoom
    // gestures (click/wheel/pointer) from hijacking the video's own native
    // controls.
    isVideoActive() {
      var data = this.images[this.active - 1];
      return !!(data && data.type === 'video');
    }

    // Set the large image with a soft opacity crossfade. Clears any auto-
    // generated srcset so .src is authoritative: image_tag emits a srcset,
    // and with it present the browser ignores .src and keeps showing the first
    // image (this was the prev/next "image does not change" bug).
    setImage(src) {
      var img = this.img;
      img.srcset = '';
      if (window.matchMedia(REDUCED).matches) {
        img.src = src;
        return;
      }
      img.classList.add('is-fading');
      var reveal = function () { img.classList.remove('is-fading'); };
      img.addEventListener('load', reveal, { once: true });
      img.src = src;
      if (img.complete) reveal();
    }

    // Immediate source swap (no crossfade): used when entering deep zoom.
    swapSrc(src) {
      if (this.img.getAttribute('src') === src) return;
      this.img.srcset = '';
      this.img.src = src;
    }

    onKeydown(e) {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          this.close();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.show(this.active - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.show(this.active + 1);
          break;
        default:
          break;
      }
    }

    toggleZoom(e) {
      if (this.isVideoActive()) return;
      if (this.zoomed) {
        this.resetZoom();
      } else {
        var data = this.images[this.active - 1];
        // Swap to the higher-res source for a crisp deep zoom.
        if (data.zoom) this.swapSrc(data.zoom);
        this.zoomed = true;
        this.scale = 2.2;
        this.stage.dataset.zoomed = 'true';
        this.pan(e);
      }
    }

    wheelZoom(e) {
      if (this.isVideoActive()) return;
      e.preventDefault();
      var data = this.images[this.active - 1];
      if (!this.zoomed && data.zoom) this.swapSrc(data.zoom);
      // Standard convention: wheel up / deltaY < 0 (away from user) zooms IN;
      // wheel down / deltaY > 0 zooms OUT. Clamp from fit (1) to ~3.5x.
      this.scale = clamp((this.scale || 1) - e.deltaY * 0.0025, 1, 3.5);
      this.zoomed = this.scale > 1.02;
      this.stage.dataset.zoomed = this.zoomed ? 'true' : 'false';
      if (this.zoomed) {
        this.pan(e);
      } else {
        this.resetZoom();
      }
    }

    pan(e) {
      if (this.isVideoActive() || !this.zoomed) return;
      var rect = this.stage.getBoundingClientRect();
      var x = clamp((e.clientX - rect.left) / rect.width, 0, 1);
      var y = clamp((e.clientY - rect.top) / rect.height, 0, 1);
      var scale = this.scale || 2.2;
      // Translate so the pointed-at point stays under the cursor.
      var tx = (0.5 - x) * rect.width * (scale - 1);
      var ty = (0.5 - y) * rect.height * (scale - 1);
      this.img.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) scale(' + scale + ')';
    }

    resetZoom() {
      this.zoomed = false;
      this.scale = 1;
      this.stage.dataset.zoomed = 'false';
      this.img.style.transform = '';
    }
  }

  // Exposed globally so sn-home-reviews can build its media lightbox on this
  // class via its own inline wiring. The PDP no longer uses it: its media
  // viewer is assets/sn-pdp-viewer.js.
  window.SnLightbox = SnLightbox;

  /* ------------------------------------------------- Scroll-fill indicator */

  class SnPdpGalleryProgress {
    constructor(track, gallery) {
      this.gallery = gallery;
      this.fill = track.querySelector('[data-sn-gallery-progress-fill]');
      if (!this.fill) return;

      this.ticking = false;
      var self = this;
      this.queue = function () {
        if (self.ticking) return;
        self.ticking = true;
        window.requestAnimationFrame(function () {
          self.update();
          self.ticking = false;
        });
      };

      window.addEventListener('scroll', this.queue, { passive: true });
      window.addEventListener('resize', this.queue);
      this.update();
    }

    // Progress is measured against the gallery column's OWN top/bottom
    // bounds (its getBoundingClientRect), not the whole page's scroll
    // percentage: 0 while the column's top has not yet reached the
    // viewport top, 1 once its bottom has scrolled past the viewport
    // bottom.
    update() {
      var rect = this.gallery.getBoundingClientRect();
      var vh = window.innerHeight;
      var total = rect.height - vh;
      var progress;
      if (total <= 0) {
        progress = rect.top <= 0 ? 1 : 0;
      } else {
        progress = clamp(-rect.top / total, 0, 1);
      }
      this.fill.style.height = (progress * 100) + '%';
    }
  }

  /* ------------------------------------------------- Gallery video playback
     5.3.1 video round. Runs on every breakpoint (mobile slides and the
     desktop lead/grid both render [data-sn-gallery-video] elements, only
     one set is visible at a time via CSS, but both exist in the DOM), so
     it is called unconditionally from init() below.

     While the media viewer is open (sn-pdp-viewer.js dispatches
     sn-pdp-viewer:open / :close on document) every page video is held
     still, and the viewer's own videos are not [data-sn-gallery-video], so
     this observer never touches them. On close the observer is re-armed:
     observe() reports the current intersection straight away, so each
     video plays or pauses exactly as if the viewer had never opened. */

  class SnPdpGalleryVideos {
    constructor() {
      this.videos = Array.prototype.slice.call(document.querySelectorAll('[data-sn-gallery-video]'));
      this.observer = null;
      this.suspended = false;
      if (!this.videos.length) return;

      var self = this;
      document.addEventListener('sn-pdp-viewer:open', function () { self.suspend(); });
      document.addEventListener('sn-pdp-viewer:close', function () { self.resume(); });

      // Respect the user's own preference outright: pause everything and
      // never wire the observer, so nothing ever auto-plays.
      if (window.matchMedia(REDUCED).matches) {
        this.videos.forEach(function (v) { v.pause(); });
        return;
      }

      this.observer = new IntersectionObserver(
        function (entries) {
          if (self.suspended) return;
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              // Autoplay promises reject under some conditions (e.g. the
              // tab backgrounded between intersecting and the play() call
              // resolving): swallow it so that isn't an unhandled
              // rejection/console error on every page load.
              entry.target.play().catch(function () {});
            } else {
              entry.target.pause();
            }
          });
        },
        { threshold: 0.25 }
      );

      this.videos.forEach(function (v) { self.observer.observe(v); });
    }

    suspend() {
      this.suspended = true;
      this.videos.forEach(function (v) { v.pause(); });
    }

    resume() {
      this.suspended = false;
      if (!this.observer) return;
      var observer = this.observer;
      observer.disconnect();
      this.videos.forEach(function (v) { observer.observe(v); });
    }
  }

  /* ------------------------------------------------- Transparent video, WebKit
     snippets/sn-pdp-alpha-video.liquid plays a WebM with an alpha channel.
     Safari (desktop) and every browser on iPhone/iPad are WebKit, which
     plays that WebM but composites the transparent area as black. With an
     HEVC .mov set, Safari picks that <source> itself and nothing needs to
     happen here. Without one, swap the video for the transparent still.

     User-agent detection is deliberate: no feature test reports whether
     alpha is composited. canPlayType('video/webm; codecs=vp9') already says
     "probably" in the Safari versions that render it black.

     This runs over EVERY [data-sn-alpha-video] on the page, the media
     viewer's slides included (the snippet renders one there too), so on
     WebKit the viewer's video is already swapped for its still before the
     viewer is ever opened. sn-pdp-viewer.js relies on that: its
     paintedMedia() skips a hidden video and uses the un-hidden still. */
  function isWebKitOnly() {
    var ua = navigator.userAgent;
    // All iOS/iPadOS browsers are WebKit, whatever they call themselves.
    if (/iPhone|iPad|iPod/.test(ua)) return true;
    // iPadOS 13+ reports as a Mac; a touch screen tells it apart from one.
    if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true;
    // Desktop Safari: the other desktop engines also put "Safari" in the UA.
    return /Safari/.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|Firefox|FxiOS/.test(ua);
  }

  function initAlphaVideos() {
    var wraps = document.querySelectorAll('[data-sn-alpha-video]');
    if (!wraps.length || !isWebKitOnly()) return;

    var warned = false;
    Array.prototype.forEach.call(wraps, function (wrap) {
      if (wrap.hasAttribute('data-has-hevc')) return;

      var video = wrap.querySelector('video');
      var still = wrap.querySelector('.sn-pdp-alpha-video__still');
      if (!video) return;

      if (!still) {
        // Once per page, not once per slot (mobile slide, lead, grid cell
        // and the viewer can all render this).
        if (!warned) {
          console.warn('[sn-pdp] Transparent video has no HEVC file and no still image set: Apple devices will show a black background. Set "Still for Apple devices" in the PDP hero section.');
          warned = true;
        }
        return;
      }

      still.removeAttribute('hidden');
      video.pause();
      // Drop the sources and reload so the browser stops fetching the WebM.
      Array.prototype.slice.call(video.querySelectorAll('source')).forEach(function (s) {
        s.parentNode.removeChild(s);
      });
      video.load();
      video.hidden = true;
    });
  }

  function init() {
    // Before the video observer below is built, so it never sees (or plays) a
    // video that is about to be swapped out.
    initAlphaVideos();
    new SnPdpGalleryVideos();

    document.querySelectorAll('[data-sn-dgal]').forEach(function (el) {
      // Scroll-linked, so only attach it on desktop: avoids needless
      // scroll-listener cost on mobile, where the track is display:none
      // anyway.
      var progressTrack = el.querySelector('[data-sn-gallery-progress]');
      if (progressTrack && window.matchMedia(DESKTOP).matches) {
        new SnPdpGalleryProgress(progressTrack, el);
      }
    });
  }

  /* ------------------------------------------------- Header-height sync
     .sn-pdp-hero__content's sticky top offset (section-sn-pdp-hero.css)
     reads --header-height off the document root. Dawn's stock sticky-
     header component sets that variable, but the header actually
     rendered on this theme (sn-header.liquid / sn-header.js) is a
     separate, unrelated implementation that never writes to it, so it
     was silently resolving to the 0px fallback, and the sticky column
     stuck underneath the real header instead of below it.

     .sn-header-section is the live header's own sticky wrapper, NOT
     the announcement bar, which is a separate, position:relative (non-
     sticky) sibling section (section-sn-announcement-bar.css) that the
     header's own height never includes. Measuring this element directly,
     rather than summing it with the announcement bar or hardcoding a
     guess, means dismissing the announcement bar cannot desync this
     value: it was never part of it. ResizeObserver (not a one-time
     measurement) keeps it correct across the header's own capsule-mode
     shrink, responsive breakpoint changes, and font-load reflow. */
  function initHeaderHeightSync() {
    var header = document.querySelector('.sn-header-section');
    if (!header) return;

    function setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    }

    setHeaderHeight();

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(setHeaderHeight).observe(header);
    } else {
      window.addEventListener('resize', setHeaderHeight);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('DOMContentLoaded', initHeaderHeightSync);
  } else {
    init();
    initHeaderHeightSync();
  }
})();
